import React, { useEffect, useState } from "react";
import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import cn from "classnames";
import * as Sentry from "@sentry/browser";
import { Link, useHistory, useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import { facebookAppId, fields } from "../../../common/parameters";

import login from "../../store/user/actions/login";
import facebookLogin from "../../store/user/actions/facebookLogin";
import googleLogin from "../../store/user/actions/googleLogin";
import { IStateTree } from "../../store/interfaces";
import routing from "../../routing";
import Icon from "../../icon";
import { setItem } from "../../cookie";
import usePrevious from "../../utils/usePrevious";
import { ILoggedOutUser } from "../../store/user/models";
import { useUser } from "../../store/user/selectors";

import Form, { Title, InputText, InputPassword, Submit, Cancel, FormElement } from "../Form";
import Method from "../Method";

import { Center } from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import { getAppFromHistory, getAppPrefix } from "../../../common/app";
import { TApp } from "../../../common/types";


const getRedirectPathname = (app: TApp) => {
    switch(app) {
        case "memory":
            return routing.choirs;
        default:
            return routing.dashboard;
    }
};

const Login = ({ close, noRecovery }: IProps) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const app = getAppFromHistory(history);
    const { locale, formatMessage } = useIntl();
    const user = useUser();;
    const loggedOutUser = useSelector(({ user }: IStateTree) => user) as ILoggedOutUser;
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [changedAfterSubmit, setChangedAfterSubmit] = useState(true);
    const [validationEmail, setValidationEmail] = useState<string | null>(null);
    const [validationPassword, setValidationPassword] = useState<string | null>(null);

    const changeEmail = (e: React.FormEvent<HTMLInputElement>) => {
        setEmail(e.currentTarget.value);
        setValidationEmail(null);
        setChangedAfterSubmit(true);
    };
    const changePassword = (e: React.FormEvent<HTMLInputElement>) => {
        setPassword(e.currentTarget.value);
        setValidationPassword(null);
        setChangedAfterSubmit(true);
    };

    const loginSuccess = () => {
        if (process.env.NODE_ENV !== "development") {
            Sentry.configureScope((scope) => {
                scope.setUser({
                    id: user._id,
                    email: user.email,
                });
            });
        }
        if (window.OneSignal) {
            window.OneSignal.push(function () {
                window.OneSignal.showNativePrompt();
            });
        }
        if (close) close();
        if (user.isStudent) {
            history.push("/" + routing.dashboardStudent);
        } else if (user.profiled) {
            history.push(
                getAppPrefix(app) +
                "/" +
                getRedirectPathname(app)
            );
        } else {
            history.push(getAppPrefix(app) + "/" + routing.signupProfile);
        }
        if (user.language !== locale) {
            setItem("language", user.language, 60 * 60 * 24 * 365);
            window.renderPageWithLanguage(user.language);
        }
    };

    const handleLogin = () => {
        if (email.length === 0) setValidationEmail("required");
        if (password.length === 0) setValidationPassword("required");
        if (email.length === 0 || password.length === 0) return;
        setChangedAfterSubmit(false);
        dispatch(login({ email, password }));
    };

    const previousPath = usePrevious(location.pathname);
    useEffect(() => {
        if (previousPath && previousPath !== location.pathname) {
            close();
        }
    }, [location.pathname, previousPath]);
    useEffect(() => {
        if (user.loggedIn) {
            loginSuccess();
        }
    }, [user]);

    const handleFacebookLogin = (res: any) => {
        if (!res.accessToken) {
            return setValidationEmail("facebookConnectionError");
        }
        dispatch(facebookLogin({ accessToken: res.accessToken, lang: locale }));
    };
    const googleLoginSuccess = (res: Omit<TokenResponse, "error" | "error_description" | "error_uri">) => {
        if (!res.access_token) {
            return setValidationEmail("googleConnectionError");
        }
        dispatch(googleLogin({ accessToken: res.access_token, lang: locale }));
    };
    const googleLoginFailure = () => {
        setValidationEmail("googleConnectionError");
    };

    const handleLoginWithGoogle = useGoogleLogin({
        onSuccess: googleLoginSuccess,
        onError: googleLoginFailure,
    });

    const toSignup = () => {
        history.push(getAppPrefix(app) + "/" + routing.signup);
        close();
    };

    return (
        <div>
            <Form>
                <h1 style={{ textAlign: 'center' }}>
                    <FormattedMessage {...messages.title} />
                </h1>
                <Method>
                    <span>
                        <FormattedMessage {...messages.withEmail} />
                    </span>
                </Method>
                <InputText
                    validation={validationEmail || (!changedAfterSubmit ? loggedOutUser.validationEmail : undefined)}
                    email
                    label={formatMessage(messages.email)}
                    value={email}
                    onChange={changeEmail}
                    disabled={!!loggedOutUser.loading}
                />
                <InputPassword
                    validation={validationPassword || (!changedAfterSubmit ? loggedOutUser.validationPassword : undefined)}
                    label={formatMessage(messages.password)}
                    value={password}
                    onChange={changePassword}
                    disabled={!!loggedOutUser.loading}
                />
                {!noRecovery && (
                    <FormElement>
                        <Center>
                            <Link
                                className="link"
                                to={getAppPrefix(app) + "/" + routing.passwordRecovery}
                                onClick={close}
                            >
                                <FormattedMessage {...messages.remindPassword} />
                            </Link>
                        </Center>
                    </FormElement>
                )}
                <Submit onClick={handleLogin} disabled={!!loggedOutUser.loading}>
                    <FormattedMessage {...messages.submit} />
                </Submit>
                <Cancel onClick={toSignup} disabled={!!loggedOutUser.loading}>
                    <FormattedMessage {...messages.signup} />
                </Cancel>
                <Method>
                    <span>
                        <FormattedMessage {...messages.withSocial} />
                    </span>
                </Method>
                <FacebookLogin
                    appId={facebookAppId}
                    fields={fields}
                    isMobile={false}
                    disableMobileRedirect
                    render={(renderProps: any) => (
                        <Cancel facebook onClick={renderProps.onClick} disabled={!!loggedOutUser.loading}>
                            <span className={cn("button-facebook", "button-facebook--dark")}>
                                <Icon i="facebook" />
                            </span>
                            {loggedOutUser.loading === "facebook" ? (
                                <FormattedMessage {...messages.facebookInProgress} />
                            ) : (
                                <FormattedMessage {...messages.facebookButton} />
                            )}
                        </Cancel>
                    )}
                    callback={handleFacebookLogin}
                />
                <Cancel google onClick={() => handleLoginWithGoogle()} disabled={!!loggedOutUser.loading}>
                    <span className={cn("button-google", "button-google--dark")}>
                        <Icon i="google" />
                    </span>
                    {loggedOutUser.loading === "google" ? (
                        <FormattedMessage {...messages.facebookInProgress} />
                    ) : (
                        <FormattedMessage {...messages.googleButton} />
                    )}
                </Cancel>
            </Form>
        </div>
    );
}

export default Login;
